import { APP_NAME, getFiscalYear } from '@solar/shared';
import { Sun } from 'lucide-react';
import { Link } from 'react-router-dom';

import { SidebarNav } from '@/layouts/SidebarNav';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Sheet, SheetContent, SheetTitle } from '@/components/ui/sheet';
import { useAuthStore } from '@/store/auth.store';
import { useUiStore } from '@/store/ui.store';
import { cn } from '@/lib/utils';

/**
 * The sidebar, in three presentations of one navigation tree: a full rail, a
 * collapsed icon rail, and a drawer below `lg`. Only the width and the nav's own
 * `collapsed` flag differ — the tree itself is built once, in `SidebarNav`.
 */

const Brand = ({ collapsed = false }: { collapsed?: boolean }) => {
  const company = useAuthStore((state) => state.user?.company);

  return (
    <Link
      to="/dashboard"
      className={cn(
        'flex h-topbar shrink-0 items-center gap-2.5 border-b border-border px-3',
        collapsed && 'justify-center px-0',
      )}
    >
      <span className="flex size-7 shrink-0 items-center justify-center rounded-md bg-primary text-primary-foreground shadow-sm">
        <Sun className="size-4" strokeWidth={2.5} />
      </span>
      {collapsed ? null : (
        <span className="min-w-0">
          <span className="block truncate text-sm font-semibold leading-tight">
            {company?.name ?? APP_NAME}
          </span>
          <span className="block truncate text-2xs text-muted-foreground">Solar ERP</span>
        </span>
      )}
    </Link>
  );
};

/**
 * Fiscal-year marker. Indian document numbering, subsidy claims and every report
 * are scoped to the financial year, so which one is open is standing context
 * rather than a detail belonging to one screen.
 */
const SidebarFooter = () => {
  const company = useAuthStore((state) => state.user?.company);
  const fiscalYear = getFiscalYear(new Date(), company?.fiscalYearStartMonth);

  return (
    <div className="flex shrink-0 items-center justify-between gap-2 border-t border-border px-3 py-2.5">
      <span className="text-2xs uppercase tracking-wider text-muted-foreground/70">
        Financial year
      </span>
      <span className="numeric text-2xs font-medium">{fiscalYear.shortLabel}</span>
    </div>
  );
};

/** Fixed sidebar for `lg` and up. Collapses to an icon rail with flyout groups. */
export const Sidebar = () => {
  const sidebarCollapsed = useUiStore((state) => state.sidebarCollapsed);

  return (
    <aside
      className={cn(
        'fixed inset-y-0 left-0 z-30 hidden flex-col border-r border-border bg-card transition-[width] duration-200 lg:flex',
        sidebarCollapsed ? 'w-sidebar-collapsed' : 'w-sidebar',
      )}
    >
      <Brand collapsed={sidebarCollapsed} />

      <ScrollArea className="flex-1">
        <SidebarNav collapsed={sidebarCollapsed} />
      </ScrollArea>

      {sidebarCollapsed ? null : <SidebarFooter />}
    </aside>
  );
};

/** Drawer variant for tablet and phone widths. Always renders the full tree. */
export const MobileSidebar = () => {
  const mobileSidebarOpen = useUiStore((state) => state.mobileSidebarOpen);
  const setMobileSidebarOpen = useUiStore((state) => state.setMobileSidebarOpen);

  return (
    <Sheet open={mobileSidebarOpen} onOpenChange={setMobileSidebarOpen}>
      <SheetContent side="left" className="flex w-72 flex-col gap-0 p-0">
        <SheetTitle className="sr-only">Navigation</SheetTitle>
        <Brand />
        <ScrollArea className="flex-1">
          {/* Tapping a link closes the drawer — on a phone it covers the page. */}
          <SidebarNav onNavigate={() => setMobileSidebarOpen(false)} />
        </ScrollArea>
        <SidebarFooter />
      </SheetContent>
    </Sheet>
  );
};
